/**
 * The calculation engine (spec section 9).
 *
 * Pure functions only — no React, no I/O, no dates. Every formula in the spec
 * lives here and nowhere else.
 *
 * Two rules govern the whole module:
 *   1. A metric that cannot be computed is `null` WITH a reason. It is never 0.
 *      (Section 22: a missing value must not let a product rank #1.)
 *   2. Division guards on `<= 0`, not just `=== 0` — a negative price or a
 *      zero-weight package is bad data, not a licence to emit Infinity.
 */

import type {
  NutritionBasis,
  NutritionFacts,
  Nutrients,
  Product,
  QuantityKind,
} from '@/types/product';
import { normalizeToPackage, normalizeToPer100 } from './normalization';

// ---------------------------------------------------------------------------
// Atwater factors (section 9)
// ---------------------------------------------------------------------------

export const KCAL_PER_G_PROTEIN = 4;
export const KCAL_PER_G_CARB = 4;
export const KCAL_PER_G_FAT = 9;

// ---------------------------------------------------------------------------
// Metric identity
// ---------------------------------------------------------------------------

export type MetricId =
  // Value metrics
  | 'proteinPerDollar'
  | 'kcalPerDollar'
  | 'carbsPerDollar'
  | 'fatPerDollar'
  | 'pricePer100kcal'
  | 'pricePer10gProtein'
  | 'pricePer100g'
  | 'pricePer100ml'
  // Nutrition-density metrics
  | 'proteinPer100kcal'
  | 'proteinPctCalories'
  | 'carbPctCalories'
  | 'fatPctCalories'
  | 'protein100'
  | 'carb100'
  | 'fat100'
  | 'carbsPer100kcal'
  | 'fatPer100kcal'
  | 'kcal100'
  | 'fibre100'
  | 'sugar100'
  | 'sodium100'
  // Raw
  | 'price'
  | 'packageSize';

/** Whether a *higher* raw number is better for ranking purposes. */
export const METRIC_DIRECTION: Record<MetricId, 'higher' | 'lower'> = {
  proteinPerDollar: 'higher',
  kcalPerDollar: 'higher',
  carbsPerDollar: 'higher',
  fatPerDollar: 'higher',
  pricePer100kcal: 'lower',
  pricePer10gProtein: 'lower',
  pricePer100g: 'lower',
  pricePer100ml: 'lower',
  proteinPer100kcal: 'higher',
  proteinPctCalories: 'higher',
  carbPctCalories: 'higher',
  fatPctCalories: 'higher',
  protein100: 'higher',
  carb100: 'higher',
  fat100: 'higher',
  carbsPer100kcal: 'higher',
  fatPer100kcal: 'higher',
  kcal100: 'higher',
  fibre100: 'higher',
  sugar100: 'lower',
  sodium100: 'lower',
  price: 'lower',
  packageSize: 'higher',
};

export interface MetricMeta {
  id: MetricId;
  label: string;
  /** Short unit string for display, e.g. "g/$". `$` is replaced by currency. */
  unit: string;
  /** Plain-English formula, shown in the UI so nothing is a black box (s.14). */
  formula: string;
  decimals: number;
}

export const METRICS: Record<MetricId, MetricMeta> = {
  proteinPerDollar: {
    id: 'proteinPerDollar',
    label: 'Protein per dollar',
    unit: 'g protein / $',
    formula: 'package protein (g) ÷ price',
    decimals: 1,
  },
  kcalPerDollar: {
    id: 'kcalPerDollar',
    label: 'Calories per dollar',
    unit: 'kcal / $',
    formula: 'package energy (kcal) ÷ price',
    decimals: 0,
  },
  carbsPerDollar: {
    id: 'carbsPerDollar',
    label: 'Carbs per dollar',
    unit: 'g carbs / $',
    formula: 'package carbohydrate (g) ÷ price',
    decimals: 1,
  },
  fatPerDollar: {
    id: 'fatPerDollar',
    label: 'Fat per dollar',
    unit: 'g fat / $',
    formula: 'package fat (g) ÷ price',
    decimals: 1,
  },
  pricePer100kcal: {
    id: 'pricePer100kcal',
    label: 'Price per 100 kcal',
    unit: '$ / 100 kcal',
    formula: 'price ÷ package energy (kcal) × 100',
    decimals: 3,
  },
  pricePer10gProtein: {
    id: 'pricePer10gProtein',
    label: 'Price per 10 g protein',
    unit: '$ / 10 g',
    formula: 'price ÷ package protein (g) × 10',
    decimals: 3,
  },
  pricePer100g: {
    id: 'pricePer100g',
    label: 'Price per 100 g',
    unit: '$ / 100 g',
    formula: 'price ÷ package weight (g) × 100',
    decimals: 3,
  },
  pricePer100ml: {
    id: 'pricePer100ml',
    label: 'Price per 100 ml',
    unit: '$ / 100 ml',
    formula: 'price ÷ package volume (ml) × 100',
    decimals: 3,
  },
  proteinPer100kcal: {
    id: 'proteinPer100kcal',
    label: 'Protein per 100 kcal',
    unit: 'g / 100 kcal',
    formula: 'protein (g) ÷ energy (kcal) × 100',
    decimals: 1,
  },
  proteinPctCalories: {
    id: 'proteinPctCalories',
    label: 'Protein % of calories',
    unit: '%',
    formula: 'protein (g) × 4 ÷ total kcal × 100',
    decimals: 0,
  },
  carbPctCalories: {
    id: 'carbPctCalories',
    label: 'Carbs % of calories',
    unit: '%',
    formula: 'carbohydrate (g) × 4 ÷ total kcal × 100',
    decimals: 0,
  },
  fatPctCalories: {
    id: 'fatPctCalories',
    label: 'Fat % of calories',
    unit: '%',
    formula: 'fat (g) × 9 ÷ total kcal × 100',
    decimals: 0,
  },
  protein100: { id: 'protein100', label: 'Protein', unit: 'g', formula: 'protein per 100 g/ml', decimals: 1 },
  carb100: { id: 'carb100', label: 'Carbs', unit: 'g', formula: 'carbohydrate per 100 g/ml', decimals: 1 },
  fat100: { id: 'fat100', label: 'Fat', unit: 'g', formula: 'fat per 100 g/ml', decimals: 1 },
  carbsPer100kcal: {
    id: 'carbsPer100kcal',
    label: 'Carbs per 100 kcal',
    unit: 'g / 100 kcal',
    formula: 'carbohydrate (g) ÷ energy (kcal) × 100',
    decimals: 1,
  },
  fatPer100kcal: {
    id: 'fatPer100kcal',
    label: 'Fat per 100 kcal',
    unit: 'g / 100 kcal',
    formula: 'fat (g) ÷ energy (kcal) × 100',
    decimals: 1,
  },
  kcal100: { id: 'kcal100', label: 'Energy', unit: 'kcal', formula: 'energy per 100 g/ml', decimals: 0 },
  fibre100: { id: 'fibre100', label: 'Fibre', unit: 'g', formula: 'fibre per 100 g/ml', decimals: 1 },
  sugar100: { id: 'sugar100', label: 'Sugar', unit: 'g', formula: 'sugar per 100 g/ml', decimals: 1 },
  sodium100: { id: 'sodium100', label: 'Sodium', unit: 'mg', formula: 'sodium per 100 g/ml', decimals: 0 },
  price: { id: 'price', label: 'Price', unit: '$', formula: 'listed price', decimals: 2 },
  packageSize: { id: 'packageSize', label: 'Package size', unit: '', formula: 'net package quantity', decimals: 0 },
};

// ---------------------------------------------------------------------------
// Metric values
// ---------------------------------------------------------------------------

export interface MetricValue {
  value: number | null;
  /** Present exactly when `value === null`. Explains the gap to the user. */
  unavailableReason: string | null;
}

export function ok(value: number): MetricValue {
  return { value, unavailableReason: null };
}

export function unavailable(reason: string): MetricValue {
  return { value: null, unavailableReason: reason };
}

/** Guarded division: refuses non-positive or non-finite denominators. */
export function safeDivide(
  numerator: number | null,
  denominator: number | null,
  reason: string,
): MetricValue {
  if (numerator === null || !Number.isFinite(numerator)) return unavailable(reason);
  if (denominator === null || !Number.isFinite(denominator) || denominator <= 0) {
    return unavailable(reason);
  }
  return ok(numerator / denominator);
}

// ---------------------------------------------------------------------------
// Energy
// ---------------------------------------------------------------------------

export interface EnergyResult {
  kcal: number | null;
  /** `labelled` is authoritative; `atwater` is computed from macros. */
  origin: 'labelled' | 'atwater' | null;
}

/**
 * Total energy for a nutrient set.
 *
 * Section 9: "Use labelled calories as the primary calorie value when
 * available." We only fall back to Atwater when a label is absent AND all three
 * macros are known — computing energy from a partial macro set would understate
 * it, which would then overstate every per-calorie value metric.
 */
export function energyOf(n: Nutrients): EnergyResult {
  if (n.kcal !== null && Number.isFinite(n.kcal) && n.kcal >= 0) {
    return { kcal: n.kcal, origin: 'labelled' };
  }
  // No Atwater reconstruction.
  //
  // Energy is a figure the manufacturer declares. Computing it from macros
  // yields a number that is not on the label and routinely differs from it —
  // fibre, polyols and label rounding all move it. A missing energy value is
  // reported as missing.
  return { kcal: null, origin: null };
}

export function proteinCalories(n: Nutrients): number | null {
  return n.protein === null ? null : n.protein * KCAL_PER_G_PROTEIN;
}
export function carbCalories(n: Nutrients): number | null {
  return n.carbohydrate === null ? null : n.carbohydrate * KCAL_PER_G_CARB;
}
export function fatCalories(n: Nutrients): number | null {
  return n.fat === null ? null : n.fat * KCAL_PER_G_FAT;
}

// ---------------------------------------------------------------------------
// Macro split
// ---------------------------------------------------------------------------

export interface MacroSplit {
  proteinPct: MetricValue;
  carbPct: MetricValue;
  fatPct: MetricValue;
  /** True only when protein, carbs AND fat are all known. */
  complete: boolean;
  /**
   * Sum of the known macro percentages. When `complete` is false this is what
   * the UI must label as "accounted for" rather than implying a full breakdown
   * (section 10).
   */
  accountedPct: number | null;
  energyOrigin: EnergyResult['origin'];
}

/**
 * Macro percentages of total energy.
 *
 * The denominator is labelled energy when available. That means the three
 * percentages need not sum to 100 — fibre, sugar alcohols, and label rounding
 * all legitimately account for the gap. We report that honestly instead of
 * normalising the numbers to look tidy.
 */
export function macroSplit(n: Nutrients): MacroSplit {
  const energy = energyOf(n);
  const noEnergy = 'Energy value is unavailable, so macro percentages cannot be calculated.';

  if (energy.kcal === null) {
    return {
      proteinPct: unavailable(noEnergy),
      carbPct: unavailable(noEnergy),
      fatPct: unavailable(noEnergy),
      complete: false,
      accountedPct: null,
      energyOrigin: null,
    };
  }
  if (energy.kcal <= 0) {
    const zero = 'This product reports zero calories, so macro percentages are undefined.';
    return {
      proteinPct: unavailable(zero),
      carbPct: unavailable(zero),
      fatPct: unavailable(zero),
      complete: false,
      accountedPct: null,
      energyOrigin: energy.origin,
    };
  }

  const pct = (cals: number | null, name: string): MetricValue =>
    cals === null ? unavailable(`${name} is not listed for this product.`) : ok((cals / energy.kcal!) * 100);

  const proteinPct = pct(proteinCalories(n), 'Protein');
  const carbPct = pct(carbCalories(n), 'Carbohydrate');
  const fatPct = pct(fatCalories(n), 'Fat');

  const known = [proteinPct.value, carbPct.value, fatPct.value].filter(
    (v): v is number => v !== null,
  );

  return {
    proteinPct,
    carbPct,
    fatPct,
    complete: known.length === 3,
    accountedPct: known.length === 0 ? null : known.reduce((a, b) => a + b, 0),
    energyOrigin: energy.origin,
  };
}

// ---------------------------------------------------------------------------
// Full per-product metric computation
// ---------------------------------------------------------------------------

export interface ProductMetrics {
  productId: string;
  currency: string | null;
  /** Which per-100 basis applies, if any. */
  per100Basis: NutritionBasis | null;
  per100: NutritionFacts | null;
  per100Reason: string | null;
  perPackage: NutritionFacts | null;
  perPackageReason: string | null;
  packageKind: QuantityKind | null;
  macros: MacroSplit;
  values: Record<MetricId, MetricValue>;
  /** Set when energy came from macros rather than a label. */
  energyOrigin: EnergyResult['origin'];
}

const NO_PRICE = 'No price is available for this product.';
const NO_NUTRITION = 'Nutrition information is not available for this product.';

/**
 * Compute every metric for one product.
 *
 * Value metrics (protein/$ etc.) use *package* totals, because that is what the
 * shopper actually pays for. Density metrics (protein %, protein/100 kcal) use
 * the per-100 basis, because that is what makes products comparable.
 */
export function computeMetrics(product: Product): ProductMetrics {
  const price = product.price?.amount ?? null;
  const validPrice = price !== null && Number.isFinite(price) && price > 0 ? price : null;
  const currency = product.price?.currency ?? null;

  const per100Res = normalizeToPer100(product);
  const packageRes = normalizeToPackage(product);
  const per100 = per100Res.facts;
  const pkg = packageRes.facts;

  const macros = macroSplit(per100?.nutrients ?? emptyRef);

  const values = {} as Record<MetricId, MetricValue>;
  const set = (id: MetricId, v: MetricValue): void => {
    values[id] = v;
  };

  // --- Raw ---------------------------------------------------------------
  set('price', validPrice === null ? unavailable(NO_PRICE) : ok(validPrice));

  const q = product.quantity;
  set(
    'packageSize',
    q === null ? unavailable('Package size is unknown.') : ok(q.netAmount),
  );

  // --- Price per physical unit -------------------------------------------
  const noSize = 'Package weight or volume is unknown, so a normalized price cannot be computed.';
  if (validPrice === null) {
    set('pricePer100g', unavailable(NO_PRICE));
    set('pricePer100ml', unavailable(NO_PRICE));
  } else if (q === null) {
    set('pricePer100g', unavailable(noSize));
    set('pricePer100ml', unavailable(noSize));
  } else {
    // price / package_weight × 100
    set(
      'pricePer100g',
      q.kind === 'mass' && q.netAmount > 0
        ? ok((validPrice / q.netAmount) * 100)
        : unavailable(
            q.kind === 'volume'
              ? 'This product is sold by volume — see price per 100 ml.'
              : noSize,
          ),
    );
    set(
      'pricePer100ml',
      q.kind === 'volume' && q.netAmount > 0
        ? ok((validPrice / q.netAmount) * 100)
        : unavailable(
            q.kind === 'mass'
              ? 'This product is sold by weight — see price per 100 g.'
              : noSize,
          ),
    );
  }

  // --- Package-total value metrics ---------------------------------------
  const pkgN = pkg?.nutrients ?? null;
  const pkgReason = packageRes.reason ?? NO_NUTRITION;
  const pkgEnergy = pkgN === null ? { kcal: null, origin: null } : energyOf(pkgN);

  const perDollar = (
    amount: number | null,
    missing: string,
  ): MetricValue => {
    if (validPrice === null) return unavailable(NO_PRICE);
    if (amount === null) return unavailable(missing);
    return safeDivide(amount, validPrice, NO_PRICE);
  };

  set('proteinPerDollar', perDollar(pkgN?.protein ?? null, pkgN ? 'Protein is not listed.' : pkgReason));
  set('carbsPerDollar', perDollar(pkgN?.carbohydrate ?? null, pkgN ? 'Carbohydrate is not listed.' : pkgReason));
  set('fatPerDollar', perDollar(pkgN?.fat ?? null, pkgN ? 'Fat is not listed.' : pkgReason));
  set('kcalPerDollar', perDollar(pkgEnergy.kcal, pkgN ? 'Energy is not listed.' : pkgReason));

  // price / kcal × 100
  set(
    'pricePer100kcal',
    validPrice === null
      ? unavailable(NO_PRICE)
      : pkgEnergy.kcal === null
        ? unavailable(pkgN ? 'Energy is not listed.' : pkgReason)
        : safeDivide(validPrice * 100, pkgEnergy.kcal, 'This product reports zero calories.'),
  );

  // price / protein × 10
  set(
    'pricePer10gProtein',
    validPrice === null
      ? unavailable(NO_PRICE)
      : pkgN?.protein == null
        ? unavailable(pkgN ? 'Protein is not listed.' : pkgReason)
        : safeDivide(
            validPrice * 10,
            pkgN.protein,
            'This product contains no protein, so price per 10 g of protein is undefined.',
          ),
  );

  // --- Density metrics (per 100 g / 100 ml) ------------------------------
  const p100 = per100?.nutrients ?? null;
  const p100Reason = per100Res.reason ?? NO_NUTRITION;
  const density = (v: number | null | undefined, name: string): MetricValue =>
    p100 === null
      ? unavailable(p100Reason)
      : v == null
        ? unavailable(`${name} is not listed for this product.`)
        : ok(v);

  set('protein100', density(p100?.protein, 'Protein'));
  set('carb100', density(p100?.carbohydrate, 'Carbohydrate'));
  set('fat100', density(p100?.fat, 'Fat'));
  set('fibre100', density(p100?.fibre, 'Fibre'));
  set('sugar100', density(p100?.sugar, 'Sugar'));
  set('sodium100', density(p100?.sodium, 'Sodium'));

  const p100Energy = p100 === null ? { kcal: null, origin: null } : energyOf(p100);
  set(
    'kcal100',
    p100 === null
      ? unavailable(p100Reason)
      : p100Energy.kcal === null
        ? unavailable('Energy is not listed for this product.')
        : ok(p100Energy.kcal),
  );

  // protein / kcal × 100 — "protein on a calorie budget"
  set(
    'proteinPer100kcal',
    p100 === null
      ? unavailable(p100Reason)
      : p100.protein == null
        ? unavailable('Protein is not listed for this product.')
        : p100Energy.kcal === null
          ? unavailable('Energy is not listed for this product.')
          : safeDivide(
              p100.protein * 100,
              p100Energy.kcal,
              'This product reports zero calories.',
            ),
  );

  // Carbs and fat on a calorie budget, mirroring proteinPer100kcal exactly so
  // the three density boards display the quantity they actually rank by.
  const per100kcal = (amount: number | null | undefined, name: string): MetricValue =>
    p100 === null
      ? unavailable(p100Reason)
      : amount == null
        ? unavailable(`${name} is not listed for this product.`)
        : p100Energy.kcal === null
          ? unavailable('Energy is not listed for this product.')
          : safeDivide(amount * 100, p100Energy.kcal, 'This product reports zero calories.');

  set('carbsPer100kcal', per100kcal(p100?.carbohydrate, 'Carbohydrate'));
  set('fatPer100kcal', per100kcal(p100?.fat, 'Fat'));

  set('proteinPctCalories', macros.proteinPct);
  set('carbPctCalories', macros.carbPct);
  set('fatPctCalories', macros.fatPct);

  return {
    productId: product.id,
    currency,
    per100Basis: per100?.basis ?? null,
    per100,
    per100Reason: per100Res.reason,
    perPackage: pkg,
    perPackageReason: packageRes.reason,
    packageKind: q?.kind ?? null,
    macros,
    values,
    energyOrigin: p100Energy.origin ?? pkgEnergy.origin,
  };
}

/** Shared immutable empty nutrient set, used when nutrition is absent. */
const emptyRef: Nutrients = {
  kcal: null,
  protein: null,
  carbohydrate: null,
  fat: null,
  fibre: null,
  sugar: null,
  saturatedFat: null,
  sodium: null,
  other: {},
};

// ---------------------------------------------------------------------------
// Display helpers
// ---------------------------------------------------------------------------

/** Which normalized-price metric applies to a product, given its package kind. */
export function primaryPriceMetric(kind: QuantityKind | null): MetricId {
  return kind === 'volume' ? 'pricePer100ml' : 'pricePer100g';
}

/**
 * Percentage difference between two values, e.g. "47% more protein".
 * Returns null when the baseline is not positive.
 */
export function percentDifference(value: number, baseline: number): number | null {
  if (!Number.isFinite(value) || !Number.isFinite(baseline) || baseline <= 0) return null;
  return ((value - baseline) / baseline) * 100;
}
